import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import type { Thread } from '@dev-streetbeat/chat/types'
import { usePostHog } from '@posthog/react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import type { AuthUser, FrontendConfig } from './types'
import { AuthProvider, useAuth } from './auth/useAuth'
import type { StoredUser } from './auth/tokenStore'
import { analyticsRouteFor, isPostHogEnabled } from './services/analytics'
import { AppShell } from './shell/AppShell'
import { ErrorBoundary } from './shell/ErrorBoundary'
import { LoginPage } from './shell/LoginPage'
import { ToastProvider, useToast } from './components/ui/toast'
import { FeatureRoute } from './components/FeatureRoute'
import { RoutePlaceholder, ShellLoadingPanel } from './shell/PagePanel'
import { ADMIN_NAV_ITEM, navItemsForFlags, routeKind, routeTitle } from './shell/routeModel'
import { HomeRoute } from './routes/HomeRoute'
import { ProposalRoute } from './routes/ProposalRoute'
import { getFeatureFlags } from './featureFlags'

const ClientsRoute = lazy(() =>
  import('./routes/ClientsRoute').then((module) => ({ default: module.ClientsRoute })),
)
const ClientDetailRoute = lazy(() =>
  import('./routes/ClientDetailRoute').then((module) => ({ default: module.ClientDetailRoute })),
)
const ProductsRoute = lazy(() =>
  import('./routes/ProductsRoute').then((module) => ({ default: module.ProductsRoute })),
)
const ProductRoute = lazy(() =>
  import('./routes/ProductRoute').then((module) => ({ default: module.ProductRoute })),
)
const ReportsRoute = lazy(() =>
  import('./routes/ReportsRoute').then((module) => ({ default: module.ReportsRoute })),
)
const ProfileRoute = lazy(() =>
  import('./routes/ProfileRoute').then((module) => ({ default: module.ProfileRoute })),
)
const SchedulesRoute = lazy(() =>
  import('./routes/SchedulesRoute').then((module) => ({ default: module.SchedulesRoute })),
)
const TodosRoute = lazy(() =>
  import('./routes/TodosRoute').then((module) => ({ default: module.TodosRoute })),
)
const AdminUsersRoute = lazy(() =>
  import('./routes/AdminUsersRoute').then((module) => ({ default: module.AdminUsersRoute })),
)
const InternalDsRoute = lazy(() =>
  import('./routes/InternalDsRoute').then((module) => ({ default: module.InternalDsRoute })),
)

export function FrontendApp({ config }: { config?: FrontendConfig }) {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <RoutedFrontendApp config={config} />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

function RoutedFrontendApp({ config }: { config?: FrontendConfig }) {
  const { user, loading } = useAuth()
  const { showToast } = useToast()
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = location.pathname || config?.currentPath || '/'
  const currentRoute = routeKind(currentPath)
  const featureFlags = getFeatureFlags(config?.featureFlags)
  const authUser = toAuthUser(user)
  const [dashboardThreadId, setDashboardThreadId] = useState<string | null>(
    dashboardThreadIdFromSearch(location.search) ?? config?.agent?.initialSessionId ?? null,
  )
  const handleDashboardThreadChange = useCallback(
    (threadId: string | null, _thread?: Thread | null) => {
      setDashboardThreadId(threadId)
      if (threadId) {
        navigate(`/?thread_id=${encodeURIComponent(threadId)}`)
      } else {
        navigate('/')
      }
    },
    [navigate],
  )

  const handleDashboardThreadDeleteStart = useCallback(
    (threadId: string) => {
      if (threadId !== dashboardThreadId) return
      setDashboardThreadId(null)
      navigate('/', { replace: true })
    },
    [dashboardThreadId, navigate],
  )

  const handleDashboardThreadDeleteError = useCallback(() => {
    showToast({
      variant: 'error',
      message: 'Impossibile eliminare la conversazione. Riprova.',
    })
  }, [showToast])

  const handleSelectedThreadUnavailable = useCallback(
    (threadId: string) => {
      if (threadId !== dashboardThreadId) return
      setDashboardThreadId(null)
      navigate('/', { replace: true })
      showToast({
        variant: 'warning',
        message: 'La conversazione non è più disponibile. Sei stato riportato alla dashboard.',
        durationMs: 5000,
      })
    },
    [dashboardThreadId, navigate, showToast],
  )

  useEffect(() => {
    if (currentPath !== '/') return
    setDashboardThreadId(dashboardThreadIdFromSearch(location.search))
  }, [currentPath, location.search])

  if (currentRoute === 'login') {
    if (!loading && authUser) {
      const nextParam = new URLSearchParams(location.search).get('next')
      const dest =
        nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : '/'
      return <Navigate to={dest} replace />
    }
    return <LoginPage />
  }
  if (loading) return <ShellLoadingPanel />
  if (!authUser) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  return (
    <AppShell
      agentConfig={config?.agent}
      authUser={authUser}
      currentPath={currentPath}
      currentRoute={currentRoute}
      dashboardThreadId={dashboardThreadId}
      navItems={navItemsForFlags(featureFlags)}
      adminNavItem={authUser.is_admin ? ADMIN_NAV_ITEM : null}
      onDashboardThreadChange={handleDashboardThreadChange}
      onDashboardThreadDeleteStart={handleDashboardThreadDeleteStart}
      onDashboardThreadDeleteError={handleDashboardThreadDeleteError}
    >
      <PostHogRouteTracker authUser={authUser} />
      <Suspense fallback={<ShellLoadingPanel />}>
        <Routes>
          <Route
            path="/"
            element={
              <HomeRoute
                config={config?.agent}
                authUser={authUser}
                threadId={dashboardThreadId}
                onThreadChange={handleDashboardThreadChange}
                onSelectedThreadUnavailable={handleSelectedThreadUnavailable}
              />
            }
          />
          <Route
            path="/clients"
            element={
              <FeatureRoute enabled={featureFlags.clientsPage}>
                <ClientsRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/clients/:clientCode"
            element={
              <FeatureRoute enabled={featureFlags.clientsPage}>
                <ClientDetailRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/products"
            element={
              <FeatureRoute enabled={featureFlags.productsPage}>
                <ProductsRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/products/:productId"
            element={
              <FeatureRoute enabled={featureFlags.productsPage}>
                <ProductRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <FeatureRoute enabled={featureFlags.reportsPage}>
                <ReportsRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/:reportId"
            element={
              <FeatureRoute enabled={featureFlags.reportsPage}>
                <ProposalRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/schedules"
            element={
              <FeatureRoute enabled={featureFlags.schedulesPage}>
                <SchedulesRoute />
              </FeatureRoute>
            }
          />
          <Route
            path="/todos"
            element={
              <FeatureRoute enabled={featureFlags.todosPage}>
                <TodosRoute />
              </FeatureRoute>
            }
          />
          <Route path="/ds" element={<InternalDsRoute />} />
          <Route path="/profile" element={<ProfileRoute authUser={authUser} />} />
          <Route
            path="/admin/users"
            element={
              authUser.is_admin ? (
                <AdminUsersRoute currentUserId={authUser.id} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="*" element={<RoutePlaceholder path={currentPath} />} />
        </Routes>
      </Suspense>
    </AppShell>
  )
}

function PostHogRouteTracker({ authUser }: { authUser: AuthUser }) {
  const location = useLocation()
  const posthog = usePostHog()

  useEffect(() => {
    if (!isPostHogEnabled || !posthog) return
    posthog.identify(`credem-user:${authUser.id}`, {
      email: authUser.email,
      name: authUser.name,
      role: authUser.role,
      is_admin: authUser.is_admin,
      has_advisor_code: Boolean(authUser.advisor_code),
    })

    const route = analyticsRouteFor(location.pathname, location.search)
    posthog.capture('$pageview', {
      $current_url: `${window.location.origin}${route.path}`,
      ...route,
      user_role: authUser.role,
      user_is_admin: authUser.is_admin,
      user_has_advisor: Boolean(authUser.advisor_code),
    })
  }, [
    authUser.advisor_code,
    authUser.email,
    authUser.id,
    authUser.is_admin,
    authUser.name,
    authUser.role,
    location.pathname,
    location.search,
    posthog,
  ])

  return null
}

function dashboardThreadIdFromSearch(search: string): string | null {
  const threadId = new URLSearchParams(search).get('thread_id')?.trim()
  return threadId || null
}

function toAuthUser(user: StoredUser | null): AuthUser | null {
  if (!user) return null
  return {
    id: user.id ?? 0,
    email: user.email,
    name: user.name ?? user.email,
    role: user.role,
    is_admin: user.is_admin,
    advisor_code: user.advisor_code,
  }
}
